(function () {
  'use strict';

  const DEFAULT_REQUEST_TIMEOUT_MS = 12000;

  function buildJsonRequest(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    };
  }

  function getTimeoutMs(opts = {}) {
    const timeoutMs = Number(opts.timeoutMs);
    return Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : DEFAULT_REQUEST_TIMEOUT_MS;
  }

  async function fetchWithTimeout(url, request, opts = {}) {
    const timeoutMs = getTimeoutMs(opts);
    if (typeof AbortController === 'undefined') {
      return fetch(url, request);
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, {
        ...request,
        signal: controller.signal,
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw new Error(`Cart request timed out after ${timeoutMs}ms`);
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Failed to fetch cart (${response.status})`);
    return response.json();
  }

  async function fetchCartForPostAdd(addedVariantId, attempts = 3) {
    const targetVariantId = Number(addedVariantId || 0);
    const maxAttempts = Math.max(1, Number(attempts) || 1);

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const candidate = await fetchCart();
        if (
          !targetVariantId ||
          (Array.isArray(candidate?.items) && candidate.items.some((item) => Number(item.variant_id) === targetVariantId))
        ) {
          return candidate;
        }
      } catch (_) {
        // Let the caller decide whether to fall back to recovery logic.
      }

      if (attempt < maxAttempts) {
        await delay(80 * attempt);
      }
    }

    return null;
  }

  async function updateCart(updates, opts = {}) {
    const hasUpdates = !!updates && Object.keys(updates).length > 0;
    const response = await fetch(
      '/cart/update.js',
      buildJsonRequest({
        updates: hasUpdates ? updates : undefined,
        // An empty string clears every code, so only send it when planned.
        discount: typeof opts.discount === 'string' ? opts.discount : undefined,
        sections: Array.isArray(opts.sections) ? opts.sections : undefined,
        sections_url: opts.sectionsUrl,
      })
    );
    if (!response.ok) throw new Error(`Failed to update cart (${response.status})`);
    return response.json();
  }

  async function updateDiscountCodes(codes, opts = {}) {
    const normalizedCodes = Array.isArray(codes)
      ? codes.map((code) => String(code || '').trim().toUpperCase()).filter(Boolean)
      : String(codes || '')
          .split(',')
          .map((code) => String(code || '').trim().toUpperCase())
          .filter(Boolean);

    const response = await fetchWithTimeout(
      '/cart/update.js',
      buildJsonRequest({
        discount: normalizedCodes.join(','),
        sections: Array.isArray(opts.sections) ? opts.sections : undefined,
        sections_url: opts.sectionsUrl,
      }),
      opts
    );
    if (!response.ok) throw new Error(`Failed to update discount (${response.status})`);
    return response.json();
  }

  window.drShopifyCartTransport = {
    fetchCart,
    fetchCartForPostAdd,
    updateCart,
    updateDiscountCodes,
  };
})();
